<template>
  <form>
    <div class="row mb-2">
      <div class="col-sm-12">
        <div class="row mt-2">
          <label class="col-sm-4 col-form-label text-capitalize-first">{{ helper.trans("labels.rewards") }}, %:</label>
          <div class="col-sm-8">
            <FormInputWrapper v-slot="parentProps" :errors="getErrors(`traderRewardFee`)">
              <input :class="parentProps.class" class="form-control"  v-model="form.traderRewardFeePercent">
            </FormInputWrapper>
          </div>
        </div>

        <div class="row mt-2">
          <label class="col-sm-4 col-form-label text-capitalize-first">{{ helper.trans("labels.start_at") }}:</label>
          <div class="col-sm-8">
            <FormInputWrapper v-slot="parentProps" :errors="getErrors(`startAt`)">
              <DatePicker :class="parentProps.class" v-model="form.startAt" mode="dateTime" :model-modifiers="{string: true}"  is24hr :masks="{modelValue: 'YYYY-MM-DD HH:mm:ss', inputDateTime24hr: 'YYYY-MM-DD HH:mm:ss'}">
                <template v-slot="{ inputValue, togglePopover, inputEvents }">
                  <input
                    class="form-control"
                    :class="parentProps.class"
                    :placeholder="helper.trans('labels.start_at')"
                    :value="inputValue"
                    @focus="togglePopover"
                    @change="inputEvents.change"
                  />
                </template>
              </DatePicker>
            </FormInputWrapper>
          </div>
        </div>

        <div class="row mt-2">
          <label class="col-sm-4 col-form-label text-capitalize-first">{{ helper.trans("labels.expire_at") }}:</label>
          <div class="col-sm-8">
            <FormInputWrapper v-slot="parentProps" :errors="getErrors(`expiredAt`)">
            <DatePicker :class="parentProps.class" v-model="form.expiredAt" mode="dateTime" :model-modifiers="{string: true}"  is24hr :masks="{modelValue: 'YYYY-MM-DD HH:mm:ss', inputDateTime24hr: 'YYYY-MM-DD HH:mm:ss'}">
              <template v-slot="{ inputValue, togglePopover, inputEvents }">
                <input
                  class="form-control"
                  :class="parentProps.class"
                  :placeholder="helper.trans('labels.expire_at')"
                  :value="inputValue"
                  @focus="togglePopover"
                  @change="inputEvents.change"
                />
              </template>
            </DatePicker>
            </FormInputWrapper>
          </div>
        </div>

        <div class="row mt-2">
          <label class="col-sm-4 col-form-label text-capitalize-first">{{ helper.trans("labels.traffic_group") }}:</label>
          <div class="col-sm-8">
            <TrafficGroupMultiselect :multiple="false" v-model="form.trafficGroup"></TrafficGroupMultiselect>
          </div>
        </div>

        <div class="row mt-2">
          <label class="col-sm-4 col-form-label text-capitalize-first">{{ helper.trans("labels.traders") }}:</label>
          <div class="col-sm-8">
            <TraderMultiselect :multiple="true" v-model="form.traders">
            </TraderMultiselect>
          </div>
        </div>



      </div>
    </div>

  </form>
</template>

<script lang="ts">
import {defineComponent, toRefs, reactive, onMounted, computed} from "vue";
import {useStore} from "vuex";
import {helper} from "@/service/CommonHelper";
import {numberFormatter} from "@/service/NumberFormatter";
import Multiselect from "vue-multiselect";
import ValidationErrorDTO from "@/models/ValidationErrorDTO";
import APIClient from "@/service/APIClient";
import {useNotification} from "@kyvg/vue3-notification";
import FormInputWrapper from "@/components/FormInputWrapper.vue";
import {DatePicker} from "v-calendar";
import moment from "moment/moment";
import TrafficGroupMultiselect from "@/components/TrafficGroupMultiselect.vue";
import {dateHelper} from "@/service/DateHelper";
import AdditionalRewardSettingsFormDTO from "@/models/form/AdditionalRewardSettingsFormDTO";
import AdditionalRewardsSettingsUpdateRequestDTO from "@/models/request/AdditionalRewardsSettingsUpdateRequestDTO";
import TraderMultiselect from "@/components/TraderMultiselect.vue";

const api = new APIClient();
const notification = useNotification()

export default defineComponent({
  props: {
    role: String
  },
  components: {
    TraderMultiselect,
    TrafficGroupMultiselect,
    DatePicker,
    Multiselect,
    FormInputWrapper,
  },
  setup() {
    const store = useStore();
    const state = reactive({
      isTeamLeadsSelectLoading: false,
      errors: {} as ValidationErrorDTO[]|any,
      form: {} as AdditionalRewardSettingsFormDTO,
    });

    const setErrors = (data: ValidationErrorDTO[]) => {
      state.errors = {};
      for (const error of data) {
        if (!state.errors.hasOwnProperty(error.field)) {
          state.errors[error.field] = [];
        }
        state.errors[error.field].push(error)
      }
    }

    const getErrors = (path: string) => {
      if (!state.errors.hasOwnProperty(path)) {
        return [];
      }

      return state.errors[path]
    }

    const save = () => {
      setErrors([]);
      const form = getForm();

      let promise;
      if (form.id) {
        const data = {
          ...form,
          traderIds: form.traders.map((trader) => trader.id),
          expiredAt: dateHelper.toUTC(form.expiredAt),
          startAt: dateHelper.toUTC(form.startAt),
          trafficGroupId: form.trafficGroup?.id,
          traderRewardFee: form.traderRewardFeePercent ? helper.percentToFloat(form.traderRewardFeePercent) : null
        } as AdditionalRewardsSettingsUpdateRequestDTO;

        promise = api.call.additionalRewardSettings.update(form.id, data)
      } else {
        const data = {
          ...form,
          traderIds: form.traders.map((trader) => trader.id),
          expiredAt: dateHelper.toUTC(form.expiredAt),
          startAt: dateHelper.toUTC(form.startAt),
          trafficGroupId: form.trafficGroup?.id,
          traderRewardFee: form.traderRewardFeePercent ? helper.percentToFloat(form.traderRewardFeePercent) : null
        } as AdditionalRewardsSettingsUpdateRequestDTO;

        promise = api.call.additionalRewardSettings.create(data)
      }

      promise
        .catch((e: any) => {
          if (e.response && e.request && e.request.status === 400 && e.response.data) {
            setErrors(e.response.data)
          } else {
            console.log(e);
          }

        })

      return promise
    }

    const hasErrors = (path: string) => {
      return state.errors[path]?.length > 0
    }

    const getForm = (): AdditionalRewardSettingsFormDTO => {
      return state.form
    }

    const setForm = (form: AdditionalRewardSettingsFormDTO) => {
      state.errors = {}
      state.form = {
        ...form,
      }
    }

    onMounted(async () => {
    })

    return {
      ...toRefs(state),
      getForm,
      setForm,
      store,
      helper,
      numberFormatter,
      setErrors,
      getErrors,
      hasErrors,
      save,
    }
  }
})
</script>
